/**
 * TestFormModal — "walk through it like a visitor" mode for the form builder.
 *
 * Renders the CURRENT draft (including unsaved tweaks) through the same
 * LeadFormRenderer the public page uses, with live validation and a simulated
 * submit, then shows the real post-submit outcome:
 *
 *   message  — the exact SuccessPanel visitors see
 *   redirect — where they'd be sent, with a button to open it
 *   booking  — which booking page they'd land on, with a button that opens it
 *              prefilled from the test answers (?name=&email=&phone=)
 *
 * NOTHING is persisted: no crm_form_submissions row, no CRM contact, no
 * notification. It's a pure front-end simulation for tweaking the experience.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Button,
  Code,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiX, FiRotateCcw, FiExternalLink } from 'react-icons/fi';
import LeadFormRenderer, {
  SuccessPanel,
  extractContactPrefill,
} from '../../LeadForm/LeadFormRenderer';
import { draftToPreviewRow } from './qrStudioUtils';

const SIMULATED_LATENCY_MS = 900;

export default function TestFormModal({ isOpen, onClose, draft, bookingPages }) {
  const [phase, setPhase] = useState('form'); // form | submitting | done
  const [testAnswers, setTestAnswers] = useState({});
  const [runKey, setRunKey] = useState(0); // remounts the renderer to clear answers

  // Fresh run every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase('form');
      setTestAnswers({});
      setRunKey((k) => k + 1);
    }
  }, [isOpen]);

  const handleTestSubmit = useCallback(async (answers) => {
    setPhase('submitting');
    await new Promise((resolve) => setTimeout(resolve, SIMULATED_LATENCY_MS));
    setTestAnswers(answers);
    setPhase('done');
    return true;
  }, []);

  const startOver = () => {
    setPhase('form');
    setTestAnswers({});
    setRunKey((k) => k + 1);
  };

  if (!draft) return null;

  const previewRow = draftToPreviewRow(draft);
  const accent = /^#[0-9a-fA-F]{6}$/.test(draft.accentColor || '') ? draft.accentColor : '#1A365D';
  const bookingPage = draft.postSubmitType === 'booking'
    ? bookingPages.find((p) => p.id === draft.postSubmitBookingPageId)
    : null;

  const openBookingWithPrefill = () => {
    if (!bookingPage) return;
    const prefill = extractContactPrefill(previewRow.fields, testAnswers);
    const params = new URLSearchParams();
    if (prefill.name) params.set('name', prefill.name);
    if (prefill.email) params.set('email', prefill.email);
    if (prefill.phone) params.set('phone', prefill.phone);
    const query = params.toString();
    window.open(`/book/${bookingPage.owner_handle}/${bookingPage.slug}${query ? `?${query}` : ''}`, '_blank', 'noopener');
  };

  const simulationNote = (
    <Text fontSize="xs" color="gray.400">
      This was a simulation — no contact, submission, or notification was created.
    </Text>
  );

  let outcome = null;
  if (phase === 'done') {
    if (draft.postSubmitType === 'redirect' && draft.postSubmitRedirectUrl) {
      outcome = (
        <SuccessPanel accent={accent} title="Submission accepted" minH="calc(100vh - 60px)"
          body="In the real flow, the visitor is instantly redirected to:">
          <VStack spacing={3} w="100%">
            <Code fontSize="sm" px={3} py={1.5} borderRadius="md" maxW="100%" wordBreak="break-all">
              {draft.postSubmitRedirectUrl}
            </Code>
            <Button size="sm" rightIcon={<FiExternalLink />} colorScheme="blue" variant="outline"
              onClick={() => window.open(draft.postSubmitRedirectUrl, '_blank', 'noopener')}>
              Open destination
            </Button>
            {simulationNote}
          </VStack>
        </SuccessPanel>
      );
    } else if (draft.postSubmitType === 'booking') {
      outcome = (
        <SuccessPanel accent={accent} title="Submission accepted" minH="calc(100vh - 60px)"
          body={bookingPage
            ? 'In the real flow, the visitor continues straight to your booking page with their details prefilled:'
            : 'This form hands off to a booking page, but none is selected (or it was deactivated) — visitors would see the thank-you message instead.'}>
          <VStack spacing={3} w="100%">
            {bookingPage && (
              <>
                <Text fontWeight="semibold" color="gray.700">{bookingPage.title}</Text>
                <Button size="sm" rightIcon={<FiExternalLink />} colorScheme="blue" variant="outline"
                  onClick={openBookingWithPrefill}>
                  Open booking page with this test data
                </Button>
              </>
            )}
            {simulationNote}
          </VStack>
        </SuccessPanel>
      );
    } else {
      // 'message' — pixel-identical to what the visitor sees, plus the sim note
      outcome = (
        <SuccessPanel
          accent={accent}
          title="You're all set!"
          body={draft.postSubmitMessage || 'Thanks! Your information has been received.'}
          minH="calc(100vh - 60px)"
        >
          {simulationNote}
        </SuccessPanel>
      );
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent borderRadius={0}>
        {/* Test-mode banner */}
        <HStack
          px={{ base: 3, md: 6 }}
          py={2.5}
          bg="purple.600"
          color="white"
          justify="space-between"
          flexWrap="wrap"
          spacing={3}
        >
          <HStack spacing={3}>
            <Badge colorScheme="whiteAlpha" variant="solid" bg="whiteAlpha.300">TEST MODE</Badge>
            <Text fontSize="sm" display={{ base: 'none', md: 'block' }}>
              You're seeing exactly what a visitor experiences. Nothing is sent to your CRM.
            </Text>
          </HStack>
          <HStack>
            <Button size="sm" variant="outline" colorScheme="whiteAlpha" color="white"
              leftIcon={<FiRotateCcw />} onClick={startOver}>
              Start over
            </Button>
            <Button size="sm" bg="white" color="purple.700" _hover={{ bg: 'purple.50' }}
              leftIcon={<FiX />} onClick={onClose}>
              Back to builder
            </Button>
          </HStack>
        </HStack>

        <ModalBody p={0} bg="gray.50" overflowY="auto">
          {phase === 'done' ? (
            outcome
          ) : (
            <LeadFormRenderer
              key={runKey}
              form={previewRow}
              onSubmit={handleTestSubmit}
              isSubmitting={phase === 'submitting'}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
